import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { newId } from '@/lib/ids'
import { enqueueCommands, type EnqueueInput } from '@/offline'
import {
  Btn,
  Cta,
  Footer,
  ItemRow,
  Keypad,
  Row,
  Strip,
  V1Screen,
  WaitCta,
} from '@/features/gate/v1/kit'
import { C, typeKey } from '@/features/gate/v1/style'
import { fmtDate, kg, shortCode, tr } from '@/features/gate/v1/text'
import type { MessageKey } from '@/i18n'
import { maxPouches } from '@/features/pack/v1/model'
import type {
  CorrectionKind,
  CorrectRunPayload,
  DecideRunPayload,
  PouchCountCorrectionPayload,
  RunCause,
} from '../commands'
import '../commands'
import { liveOutputs, runParentIds, type ProcessState, type RunView } from '../projection'
import { classifyCode } from '../model/rules'
import { correctedFigures } from '../pages/InvestigatePage'
import {
  balanceProblem,
  CAUSE_GROUPS,
  canAcceptShortfall,
  withinLossLimit,
  freshCodeV1,
  type CauseGroup,
} from './model'
import {
  Grid2,
  LeadBox,
  NextArrow,
  outputLabel,
  PHeader,
  PickTile,
  SaveError,
  saveFailed,
  SavedLine,
  SackChip,
  stepLabel,
  TagChip,
} from './ui'
import { flaggedRuns } from './Home'
import { PScanner, refusalText, ScalePad, useScaleEntry, WithSite } from './parts'

interface Draft {
  cause: RunCause | null
  corrections: CorrectRunPayload[]
  /** A held PACK run's corrected pouch count: sent with decide_run CORRECTED. */
  pouch: PouchCountCorrectionPayload | null
}

/** The step code of a pack-hall run (the server's PACK_STEP). */
const PACK_STEP = 'PACK'

/** V1-F6-B1.b — a supervisor checks a flagged run: corrections first, then the cause, re-check. */
export function V1Investigate() {
  return <WithSite>{(state) => <Flow state={state} />}</WithSite>
}

function Flow({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  // Opened from the packing hall (?from=pack) the check returns to the pack screens.
  const [params] = useSearchParams()
  const home = params.get('from') === 'pack' ? '/m/pack' : '/m/process'
  const [runId, setRunId] = useState<string | null>(null)
  const runs = flaggedRuns(state)
  const run = runId ? state.runs.get(runId) : runs.length === 1 ? runs[0] : undefined
  if (run)
    return (
      <Investigate
        key={run.id}
        state={state}
        run={run}
        onBack={() => (runs.length > 1 ? setRunId(null) : navigate(home))}
      />
    )
  return (
    <V1Screen
      header={
        <PHeader onBack={() => navigate(home)} icon="search" title={tr('process.v1.inv.list')} />
      }
    >
      {runs.length === 0 && <Strip icon="ok">{tr('process.v1.inv.none')}</Strip>}
      {runs.map((r) => (
        <ItemRow
          key={r.id}
          padding="8px 10px"
          onClick={() => setRunId(r.id)}
          lead={<LeadBox icon="machine" />}
          title={`${state.machines.get(r.equipment_id)?.code ?? ''} · ${stepLabel(state, r.step_code)}`}
          line={`${r.code}${r.farmer_code ? ` · #${r.farmer_code}` : ''}`}
          right={<NextArrow />}
        />
      ))}
    </V1Screen>
  )
}

type Mode = {
  kind: CorrectionKind
  key?: string
  code?: string
  entry?: 'SCAN' | 'TYPED'
  container_id?: string | null
}

function Investigate({
  state,
  run,
  onBack,
}: {
  state: ProcessState
  run: RunView
  onBack: () => void
}) {
  const actor = useActor()
  const [d, setD] = useState<Draft>({ cause: null, corrections: [], pouch: null })
  const [view, setView] = useState<'list' | 'cause' | 'pouch'>('list')
  const [mode, setMode] = useState<Mode | null>(null)
  const [group, setGroup] = useState<CauseGroup | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const step = state.steps.get(run.step_code)
  const limit = `${(step?.loss_limit_pct ?? 0).toFixed(1)}%`
  const before = correctedFigures(state, run, [])
  const after = correctedFigures(state, run, d.corrections)
  const outSum = (f: typeof before) => f.flush_g + f.product_g + f.byproduct_g + f.reject_g
  const pouch = pouchFacts(state, run)
  // A PACK run's pouches are not sacks on this phone: its figures come from the server's balance,
  // moved by the pouch count noted here.
  const packShort =
    run.step_code === PACK_STEP && run.balance && run.balance.output_g != null
      ? run.balance.input_g - run.balance.output_g
      : null
  const pouchDelta_g =
    d.pouch && pouch?.current != null && pouch.netG
      ? (d.pouch.pouches_good - pouch.current) * pouch.netG
      : 0
  const shortWas = packShort ?? before.input_g - outSum(before)
  const shortNow = packShort !== null ? packShort - pouchDelta_g : after.input_g - outSum(after)
  const changes = d.corrections.length + (d.pouch ? 1 : 0)
  const sack =
    state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0] ?? null
  const outputs = liveOutputs(run)
  const reweighTare = mode?.container_id
    ? (outputs.find((o) => o.container_id === mode.container_id)?.tare_g ?? 0)
    : (sack?.tare_g ?? 0)
  const scale = useScaleEntry(state, reweighTare)
  const held = run.open_exceptions.length > 0 && run.status === 'EXCEPTION'
  // A bag whose weight this phone does not know: no kilos are shown (never counted as 0).
  const unknown = after.unknown_inputs > 0
  const machine = state.machines.get(run.equipment_id)

  if (run.operator_id === actor.user_id || run.cleanout_by_id === actor.user_id)
    return (
      <V1Screen
        header={<PHeader onBack={onBack} icon="search" title={tr('process.v1.inv.title')} />}
      >
        <Strip icon="bang">{tr('process.v1.inv.own')}</Strip>
      </V1Screen>
    )

  if (sent)
    return (
      <V1Screen
        header={<PHeader onBack={onBack} icon="search" title={tr('process.v1.inv.title')} />}
        footer={
          <Footer>
            <Cta label={tr('process.v1.closed.home')} icon="home" lead onClick={onBack} />
          </Footer>
        }
      >
        <SavedLine>{tr('process.v1.cause.sent')}</SavedLine>
      </V1Screen>
    )

  const record = (
    c: Omit<CorrectRunPayload, 'correction_id' | 'run_id' | 'exception_id' | 'reason_code'>,
    line: string,
  ) => {
    const exception =
      run.open_exceptions.find((e) => e.type === 'BALANCE_OUT_OF_BAND') ?? run.open_exceptions[0]
    setD((x) => ({
      ...x,
      corrections: [
        ...x.corrections,
        // The cause is set on every correction when the re-check is sent.
        {
          ...c,
          correction_id: newId(),
          run_id: run.id,
          exception_id: exception?.id ?? null,
          reason_code: 'UNEXPLAINED_LOSS',
        },
      ],
    }))
    setSaved(line)
    setMode(null)
    scale.reset()
  }

  // F6-B1.b — दोबारा तौलें / छूटी बोरी / बिना स्कैन
  if (mode) {
    const title =
      mode.kind === 'REWEIGH_OUTPUT'
        ? 'process.v1.rew.title'
        : mode.kind === 'ADD_OUTPUT'
          ? 'process.v1.rew.missed'
          : 'process.v1.rew.noScan'
    const back = () => (scale.reset(), setMode(null))
    // ADD_OUTPUT: which output first.
    if (mode.kind === 'ADD_OUTPUT' && !mode.key)
      return (
        <V1Screen
          header={<PHeader onBack={back} icon="scale" title={tr('process.v1.rew.which')} />}
        >
          {(step?.outputs ?? []).map((o) => (
            <ItemRow
              key={o.key}
              padding="8px 10px"
              onClick={() => setMode({ ...mode, key: o.key })}
              lead={<LeadBox icon="sack" />}
              title={outputLabel(state, run.step_code, o.key)}
              right={<NextArrow />}
            />
          ))}
        </V1Screen>
      )
    // Scan the sack (re-weigh), a fresh tag (missed sack) or the tag from the tin (bag in without a scan).
    if (!mode.code) {
      const onCode = (code: string, entry: 'SCAN' | 'TYPED') => {
        if (mode.kind === 'REWEIGH_OUTPUT') {
          const o = outputs.find((x) => x.qr_payload.toUpperCase() === code)
          if (!o) return refusalText('NOT_THIS_BATCH')
          setMode({ ...mode, code, entry, container_id: o.container_id, key: o.stream_key })
          return null
        }
        if (mode.kind === 'ADD_OUTPUT') {
          const v = freshCodeV1(state, code, 'OUTPUT_BAG', actor.user_id)
          if (!v.ok) return refusalText(v.reason)
          setMode({ ...mode, code: v.value, entry, container_id: null })
          return null
        }
        const c = classifyCode(state, code)
        if (c.kind !== 'BAG') return refusalText('TAG_WRONG_KIND')
        if (!run.origin_unit_ids.includes(c.bag.unit_id)) return refusalText('OTHER_FARMER')
        if (run.inputs.some((i) => i.container_id === c.bag.container_id))
          return refusalText('DUPLICATE')
        record(
          {
            kind: 'ADD_INPUT',
            qr_payload: code,
            entry,
            container_id: c.bag.container_id,
            new_container_id: null,
            new_unit_id: null,
            stream_key: null,
            sack_type_id: null,
            weight_read_g: null,
          },
          tr('process.v1.tag', { no: shortCode(code) }),
        )
        return null
      }
      return (
        <V1Screen
          header={
            <PHeader
              onBack={back}
              icon={mode.kind === 'ADD_INPUT' ? 'scan' : 'tag'}
              title={tr(title as MessageKey)}
            />
          }
          footer={
            <Footer>
              <WaitCta>
                {tr(
                  mode.kind === 'REWEIGH_OUTPUT'
                    ? 'process.v1.rew.scanSack'
                    : mode.kind === 'ADD_OUTPUT'
                      ? 'process.v1.rew.newTag'
                      : 'process.v1.rew.scanTin',
                )}
              </WaitCta>
            </Footer>
          }
        >
          <PScanner
            state={state}
            kind={
              mode.kind === 'ADD_OUTPUT'
                ? 'OUTPUT_BAG'
                : mode.kind === 'ADD_INPUT'
                  ? 'BAG'
                  : 'OUTPUT_BAG'
            }
            hint={tr(
              mode.kind === 'REWEIGH_OUTPUT'
                ? 'process.v1.rew.scanSack'
                : mode.kind === 'ADD_OUTPUT'
                  ? 'process.v1.rew.newTag'
                  : 'process.v1.rew.scanTin',
            )}
            onCode={onCode}
          />
        </V1Screen>
      )
    }
    // The new weight (blind: the old one stays hidden).
    const saveWeight = () => {
      const r = scale.submit()
      if (!r || !mode.code) return
      const tare = mode.kind === 'ADD_OUTPUT' ? (sack?.tare_g ?? 0) : reweighTare
      const sameUnit = mode.key
        ? outputs.find((o) => o.stream_key === mode.key)?.unit_id
        : undefined
      record(
        {
          kind: mode.kind,
          qr_payload: mode.code,
          entry: mode.entry ?? 'SCAN',
          container_id: mode.kind === 'REWEIGH_OUTPUT' ? (mode.container_id ?? null) : null,
          new_container_id: mode.kind === 'ADD_OUTPUT' ? newId() : null,
          new_unit_id: mode.kind === 'ADD_OUTPUT' ? (sameUnit ?? newId()) : null,
          stream_key: mode.kind === 'ADD_OUTPUT' ? (mode.key ?? null) : null,
          sack_type_id: mode.kind === 'ADD_OUTPUT' ? (sack?.id ?? null) : null,
          weight_read_g: r.read_g,
        },
        `${tr('process.v1.tag', { no: shortCode(mode.code) })} · ${kg(r.read_g - tare)} ${tr('process.v1.kgUnit')}`,
      )
    }
    return (
      <V1Screen
        main={{ padding: '6px 12px', gap: 6 }}
        header={<PHeader onBack={back} icon="scale" title={tr(title as MessageKey)} />}
        footer={
          <Footer dense>
            {scale.value ? (
              <Cta label={tr('process.v1.weigh.save')} icon="ok" onClick={saveWeight} />
            ) : (
              <WaitCta>{tr('process.v1.weigh.wait')}</WaitCta>
            )}
          </Footer>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>
          <TagChip code={mode.code} />
          {sack && <SackChip name={sack.name} tare={kg(reweighTare)} />}
        </div>
        <ScalePad label={tr('process.v1.rew.new')} entry={scale} />
      </V1Screen>
    )
  }

  const recheck = async () => {
    if (!d.cause || changes === 0) return
    const cause = d.cause
    setSaving(true)
    setErr(null)
    try {
      const inputs: EnqueueInput[] = d.corrections.map((c, i) => ({
        id: c.correction_id,
        type: 'correct_run',
        payload: { ...c, reason_code: cause },
        actorId: actor.user_id,
        ...(i === 0 ? { parentIds: runParentIds(run) } : {}),
      }))
      // POUCH_COUNT: the corrected count, then the problem closed as CORRECTED after it when the
      // run balances with it. Still out: the run stays held (a loss may then be accepted).
      const problemNow = balanceProblem(run)
      const inputG = run.balance?.input_g ?? before.input_g
      const lossPct = run.balance?.loss_limit_pct ?? step?.loss_limit_pct ?? 0
      if (d.pouch && problemNow) {
        const pc: PouchCountCorrectionPayload = {
          ...d.pouch,
          exception_id: problemNow.id,
          reason_code: cause,
        }
        const decide: DecideRunPayload = {
          run_id: run.id,
          exception_id: problemNow.id,
          reason_code: cause,
          decision: 'CORRECTED',
          note: null,
        }
        inputs.push({
          id: pc.correction_id,
          type: 'correct_run',
          payload: pc,
          actorId: actor.user_id,
          ...(inputs.length === 0 ? { parentIds: runParentIds(run) } : {}),
        })
        if (withinLossLimit(inputG, shortNow, lossPct))
          inputs.push({
            id: newId(),
            type: 'decide_run',
            payload: decide,
            actorId: actor.user_id,
            parentIds: [pc.correction_id],
          })
      }
      await enqueueCommands(inputs)
      setSent(true)
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  // F6-B1.c — nothing to correct and a real loss: the supervisor accepts it here.
  const problem = balanceProblem(run)
  const canAccept =
    !!problem && !unknown && changes === 0 && canAcceptShortfall(problem.kinds, shortNow, d.cause)
  const accept = async () => {
    if (!problem || !d.cause || !canAccept) return
    setSaving(true)
    setErr(null)
    try {
      const payload: DecideRunPayload = {
        run_id: run.id,
        exception_id: problem.id,
        reason_code: d.cause,
        decision: 'ACCEPT_SHORTFALL',
        note: null,
      }
      await enqueueCommands([
        {
          id: newId(),
          type: 'decide_run',
          payload,
          actorId: actor.user_id,
          parentIds: runParentIds(run),
        },
      ])
      setSent(true)
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  // पाउच की गिनती ठीक करें (a PACK run held for a SURPLUS, its pack lot closed)
  if (view === 'pouch' && pouch) {
    const exception = balanceProblem(run)
    return (
      <PouchBoard
        facts={pouch}
        initial={d.pouch}
        onBack={() => setView('list')}
        onSave={(good, spoiled, destroyed) => {
          setD((x) => ({
            ...x,
            // A count typed wrong at the close: the usual cause, changeable on the next screen.
            cause: x.cause ?? 'TYPING_ERROR',
            pouch: {
              correction_id: x.pouch?.correction_id ?? newId(),
              run_id: run.id,
              exception_id: exception?.id ?? null,
              reason_code: x.cause ?? 'TYPING_ERROR',
              kind: 'POUCH_COUNT',
              pouches_good: good,
              ...(spoiled !== null ? { labels_spoiled: spoiled } : {}),
              ...(destroyed !== null ? { labels_destroyed: destroyed } : {}),
            },
          }))
          if (!d.cause) setGroup('WEIGHT')
          setSaved(tr('process.v1.inv.pouchSaved', { count: good }))
          setView('list')
        }}
      />
    )
  }

  // F6-B1.b — कारण चुनें, दोबारा जाँचें
  if (view === 'cause') {
    const groups = Object.keys(CAUSE_GROUPS) as CauseGroup[]
    const groupIcon = {
      WEIGHT: 'scale',
      MACHINE: 'machine',
      MIXING: 'mixing',
      UNKNOWN: 'search',
    } as const
    const details = group ? CAUSE_GROUPS[group] : []
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => setView('list')}
            icon="search"
            title={tr('process.v1.cause.title')}
          />
        }
        footer={
          <Footer>
            <SaveError text={err} />
            {d.cause && changes > 0 ? (
              <Cta
                label={tr('process.v1.cause.recheck')}
                icon="search"
                busy={saving}
                onClick={() => void recheck()}
              />
            ) : canAccept ? (
              <Cta
                label={tr('process.v1.cause.accept')}
                icon="ok"
                busy={saving}
                onClick={() => void accept()}
              />
            ) : (
              <WaitCta onClick={d.cause ? () => setView('list') : undefined}>
                {tr(d.cause ? 'process.v1.inv.reweigh' : 'process.v1.cause.wait')}
              </WaitCta>
            )}
          </Footer>
        }
      >
        {saved && <SavedLine>{saved}</SavedLine>}
        {unknown ? (
          <Strip icon="bang">{tr('process.v1.inv.unknown')}</Strip>
        ) : (
          <b style={{ fontSize: 18 }}>
            {tr('process.v1.cause.now', { was: kg(shortWas), now: kg(shortNow) })}
          </b>
        )}
        {d.cause && changes === 0 && !canAccept && !unknown && (
          <Strip icon="bang">{tr('process.v1.cause.noAccept')}</Strip>
        )}
        <Grid2>
          {groups.map((g) => (
            <PickTile
              key={g}
              pressed={group === g}
              icon={groupIcon[g]}
              onClick={() => {
                setGroup(g)
                const list = CAUSE_GROUPS[g]
                setD((x) => ({ ...x, cause: list.length === 1 ? list[0] : null }))
              }}
            >
              {tr(`process.v1.cause.${g}` as MessageKey)}
            </PickTile>
          ))}
        </Grid2>
        {details.length > 1 && (
          <>
            <b style={{ fontSize: 17 }}>{tr('process.v1.cause.which')}</b>
            <Grid2>
              {details.map((c) => (
                <PickTile
                  key={c}
                  pressed={d.cause === c}
                  onClick={() => setD((x) => ({ ...x, cause: c }))}
                >
                  {tr(`process.v1.cause.${c}` as MessageKey)}
                </PickTile>
              ))}
            </Grid2>
          </>
        )}
        <Row>
          <Btn icon="scale" onClick={() => setMode({ kind: 'REWEIGH_OUTPUT' })}>
            {tr('process.v1.cause.weighMore')}
          </Btn>
          <Btn icon="scan" onClick={() => setMode({ kind: 'ADD_INPUT' })}>
            {tr('process.v1.cause.fromTin')}
          </Btn>
        </Row>
      </V1Screen>
    )
  }

  // F6-B1.b — रन जाँचें: corrections first
  const rows: Array<{ kind: CorrectionKind; key: MessageKey; icon: 'scale' | 'sack' | 'scan' }> = [
    { kind: 'REWEIGH_OUTPUT', key: 'process.v1.inv.reweigh', icon: 'scale' },
    { kind: 'ADD_OUTPUT', key: 'process.v1.inv.missed', icon: 'sack' },
    { kind: 'ADD_INPUT', key: 'process.v1.inv.noScan', icon: 'scan' },
  ]
  return (
    <V1Screen
      header={<PHeader onBack={onBack} icon="search" title={tr('process.v1.inv.title')} />}
      footer={
        <Footer>
          <Cta
            label={tr('process.v1.inv.nextCause')}
            icon="next"
            onClick={() => setView('cause')}
          />
        </Footer>
      }
    >
      {saved && <SavedLine>{saved}</SavedLine>}
      <ItemRow
        padding="8px 10px"
        lead={<LeadBox icon="user" />}
        title={[run.farmer_code ? `#${run.farmer_code}` : null, machine?.code]
          .filter(Boolean)
          .join(' · ')}
        line={[
          run.ended_at ? fmtDate(run.ended_at) : null,
          tr(held ? 'process.v1.inv.held' : 'process.v1.inv.notHeld'),
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      {unknown ? (
        <Strip icon="bang">{tr('process.v1.inv.unknown')}</Strip>
      ) : (
        <b style={{ fontSize: 18, color: C.bad }}>
          {shortNow >= 0
            ? tr('process.v1.inv.less', { kg: kg(shortNow), pct: limit })
            : tr('process.v1.inv.more', { kg: kg(-shortNow), pct: limit })}
        </b>
      )}
      {rows.map((r) => (
        <ItemRow
          key={r.kind}
          padding="8px 10px"
          onClick={() => setMode({ kind: r.kind })}
          lead={<LeadBox icon={r.icon} />}
          title={tr(r.key)}
          right={<NextArrow />}
        />
      ))}
      {pouch?.lotOpen && <Strip icon="bang">{tr('process.v1.inv.pouchLotOpen')}</Strip>}
      {pouch && !pouch.lotOpen && (
        <ItemRow
          padding="8px 10px"
          onClick={() => setView('pouch')}
          border={d.pouch ? C.border : C.acc}
          borderWidth={d.pouch ? 1.5 : 2}
          lead={<LeadBox icon="pencil" />}
          title={tr('process.v1.inv.pouch')}
          line={
            d.pouch
              ? tr('process.v1.inv.pouchSaved', { count: d.pouch.pouches_good })
              : pouch.current !== null
                ? tr('process.v1.inv.pouchNow', { count: pouch.current })
                : undefined
          }
          right={<NextArrow />}
        />
      )}
      {changes > 0 && <Strip icon="ok">{tr('process.v1.inv.recorded', { count: changes })}</Strip>}
    </V1Screen>
  )
}

/** What the pouch-count correction needs to know about a held PACK run (null: not offered). */
interface PouchFacts {
  /** The good pouches the pack lot has now (null: the lot is not on this phone). */
  current: number | null
  /** The most pouches the kilos tipped in can fill (null: input or pack size not here). */
  max: number | null
  netG: number | null
  /** The pack lot is known here and not CLOSED: the server refuses the correction. */
  lotOpen: boolean
  /** More pouches than the sacks tipped in can fill (else fewer than they should). */
  surplus: boolean
}

/**
 * A PACK run held for its balance offers the pouch count correction: a SURPLUS (more pouches than
 * the sacks tipped in can fill) or a shortfall (pouches miscounted low). The pack lot and pack size
 * come from the pack hall's copy when this phone has it.
 */
function pouchFacts(state: ProcessState, run: RunView): PouchFacts | null {
  if (run.step_code !== PACK_STEP || run.status !== 'EXCEPTION') return null
  const problem = balanceProblem(run)
  if (!problem) return null
  const packRun = state.packRuns.get(run.id)
  const lot =
    (packRun?.pack_lot ? (state.packLots.get(packRun.pack_lot.id) ?? packRun.pack_lot) : null) ??
    [...state.packLots.values()].find((l) => l.run_id === run.id) ??
    null
  const netG = packRun?.sku.net_g ?? lot?.net_g ?? null
  const unknownIn = run.inputs.some((i) => i.weight_g === null)
  const input_g =
    run.balance?.input_g ??
    (unknownIn ? null : run.inputs.reduce((a, i) => a + (i.weight_g ?? 0), 0))
  // Leftover and spillage are the run's sacks; the pouches are not.
  const sacks_g = liveOutputs(run).reduce((a, o) => a + o.net_g, 0)
  const limit = run.balance?.loss_limit_pct ?? state.steps.get(PACK_STEP)?.loss_limit_pct
  return {
    current: lot?.pouch_count ?? null,
    max: maxPouches(input_g, netG, sacks_g, 0, limit),
    netG,
    lotOpen: !!lot && lot.status !== 'CLOSED',
    surplus: problem.kinds.includes('SURPLUS'),
  }
}

type PouchKey = 'good' | 'spoiled' | 'destroyed'

const POUCH_ROWS: Array<{ key: PouchKey; label: MessageKey }> = [
  { key: 'good', label: 'process.v1.inv.pouchGood' },
  { key: 'spoiled', label: 'process.v1.inv.pouchSpoiled' },
  { key: 'destroyed', label: 'process.v1.inv.pouchDestroyed' },
]

/**
 * पाउच की गिनती: good pouches pre-filled with the most that fit (a surplus) or the count now (a
 * shortfall), label counts optional.
 */
function PouchBoard({
  facts,
  initial,
  onBack,
  onSave,
}: {
  facts: PouchFacts
  initial: PouchCountCorrectionPayload | null
  onBack: () => void
  onSave: (good: number, spoiled: number | null, destroyed: number | null) => void
}) {
  const prefill = facts.surplus ? facts.max : facts.current
  const [vals, setVals] = useState<Record<PouchKey, string>>(() => ({
    good: initial ? String(initial.pouches_good) : prefill !== null ? String(prefill) : '',
    spoiled: initial?.labels_spoiled != null ? String(initial.labels_spoiled) : '',
    destroyed: initial?.labels_destroyed != null ? String(initial.labels_destroyed) : '',
  }))
  const [active, setActive] = useState<PouchKey>('good')
  // The pre-filled figure is replaced by the first digit typed, not appended to.
  const [fresh, setFresh] = useState(!initial && prefill !== null)
  const onKey = (k: string) => {
    const replace = fresh && active === 'good' && k !== '⌫'
    setFresh(false)
    setVals((v) => ({
      ...v,
      [active]: typeKey(replace ? '' : v[active], k, { decimals: 0, max: 7 }),
    }))
  }
  const num = (v: string) => (v === '' ? null : Number(v))
  const good = num(vals.good)
  const over = good !== null && facts.max !== null && good > facts.max
  return (
    <V1Screen
      main={{ padding: '8px 12px', gap: 6 }}
      header={
        <PHeader
          onBack={onBack}
          icon="pencil"
          title={tr('process.v1.inv.pouchTitle')}
          sub={
            facts.current !== null
              ? tr('process.v1.inv.pouchNow', { count: facts.current })
              : undefined
          }
        />
      }
      footer={
        <Footer dense>
          {good !== null && !over ? (
            <Cta
              label={tr('process.v1.inv.pouchSave')}
              icon="ok"
              onClick={() => onSave(good, num(vals.spoiled), num(vals.destroyed))}
            />
          ) : (
            <WaitCta>
              {over
                ? tr('process.v1.inv.pouchOver', { max: facts.max ?? 0 })
                : tr('process.v1.inv.pouchWait')}
            </WaitCta>
          )}
        </Footer>
      }
    >
      {facts.max !== null && (
        <Strip icon="info">{tr('process.v1.inv.pouchMax', { max: facts.max })}</Strip>
      )}
      {POUCH_ROWS.map((r) => {
        const on = active === r.key
        const v = vals[r.key]
        const bad = r.key === 'good' && over
        return (
          <ItemRow
            key={r.key}
            padding="6px 10px"
            onClick={() => (setActive(r.key), setFresh(false))}
            border={on ? C.acc : C.rule}
            borderWidth={on ? 2 : 1.5}
            title={<span style={{ fontSize: 18 }}>{tr(r.label)}</span>}
            right={
              <span
                style={{
                  minWidth: 72,
                  height: 44,
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  border: on ? `2px solid ${bad ? C.bad : C.acc}` : `1.5px solid ${C.border}`,
                  background: C.surface,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}
              >
                {v !== '' ? (
                  <b style={{ fontSize: 24, color: bad ? C.bad : C.ink }}>{v}</b>
                ) : (
                  <span style={{ fontSize: 20, color: C.muted }}>—</span>
                )}
                {on && (
                  <span aria-hidden="true" style={{ width: 3, height: 26, background: C.acc }} />
                )}
              </span>
            }
          />
        )
      })}
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Screen>
  )
}
